import React, { useState, useRef, useEffect } from 'react'
import { Recipient } from '../../../common/types/form'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import globalConfirmation from 'live/services/global-confirmation'
import GlobalNotification from 'live/services/global-notification'
import { ModalRecipientGroup } from './ModalRecipientGroup'
import { FileUploaderToRecipient } from './FileUploaderToRecipient'
import { resolveValidations } from '../../validations'
import { Notification } from '../../../common/components/Notification/Notification'
import { MenuItem, Dropdown } from 'react-bootstrap'
import exportRecipientsToCsv from './helper/exportRecipientsToCsv'
import { ActionSearch } from './helper/types'
import listenForOutsideClicks from '../../../common/listenForOutsideClicks'
import Toggle from '../../../common/components/SimpleInputs/Toggle'

const Search = require('live/components/lists/ui/search')(ActionSearch)

interface RecipientGroupsProps {
    formObject: Recipient[]
    onChange: (formObject: Recipient[]) => void
}

export const RecipientGroups = ({ formObject, onChange }: RecipientGroupsProps): JSX.Element => {
    const [editingGroupModal, setEditingGroupModal] = useState<{ showing: boolean; i: number }>({
        showing: false,
        i: 0
    })

    const [newRecipientModal, setNewRecipientModal] = useState<boolean>(false)
    const [showDropdown, setShowDropdown] = useState<boolean>(false)
    const [recipientFilter, setRecipientFilter] = useState<string>('')
    const [localRecipientGroups, setLocalRecipientGroups] = useState<Recipient[]>([])
    const refMenu = useRef()

    useEffect(() => {
        if (formObject !== undefined) setLocalRecipientGroups([...formObject])
    }, formObject)

    useEffect(
        listenForOutsideClicks(showDropdown, refMenu, () => setShowDropdown(false)),
        [showDropdown]
    )

    return (
        <StyledRecipientGroups className={'recipientGroups'} tabIndex={0}>
            <div className="recipientGroups--pageHeader">
                <Dropdown id="dropdown" open={showDropdown} ref={refMenu}>
                    <div className="recipientGroups--pageHeader__dropdownButton">
                        <button
                            className="btn recipientGroups--pageHeader__dropdownButton__btnAddRecipientGroup"
                            onClick={() => {
                                setNewRecipientModal(true)
                            }}
                        >
                            <span className="recipientGroups--pageHeader__dropdownButton__btnAddRecipientGroup__btnText">
                                {i18n('New Group')}
                            </span>
                        </button>
                        <button
                            className="btn recipientGroups--pageHeader__dropdownButton__btnAddRecipientGroup--dropicon"
                            onClick={() => setShowDropdown(!showDropdown)}
                        >
                            <i className={`fas fa-caret-${showDropdown ? 'up' : 'down'}`} />
                        </button>
                    </div>
                    <Dropdown.Menu aria-labelledby="dropdownMenuButton">
                        <div className="recipientGroups--pageHeader__dropdown__menuItem1">
                            <button
                                className="text-left"
                                onClick={() => {
                                    setNewRecipientModal(true)
                                }}
                            >
                                <i className="far fa-plus" />
                                {i18n('Create Group')}
                            </button>
                        </div>
                        <MenuItem divider />
                        <div className="recipientGroups--pageHeader__dropdown__menuItem2">
                            <FileUploaderToRecipient
                                currentSavedRecipients={localRecipientGroups}
                                onFileChange={newRecipientList => {
                                    const validations = resolveValidations('RECIPIENT GROUPS', newRecipientList)
                                    if (!validations.length) {
                                        setLocalRecipientGroups([...newRecipientList])
                                        onChange([...newRecipientList])
                                    } else {
                                        GlobalNotification.error(
                                            <Notification
                                                title={i18n(
                                                    'Fail trying to import invalid recipient groups, you need to fix the following issues: '
                                                )}
                                            >
                                                {i18n('The following entered fields are invalid: ')}
                                                {validations.map((value, index) => (
                                                    <tr key={index}>{value}</tr>
                                                ))}
                                            </Notification>,
                                            3
                                        )
                                    }
                                }}
                            />
                        </div>
                    </Dropdown.Menu>
                </Dropdown>

                <div className="recipientGroups--pageHeader__searchBar">
                    <Search
                        placeholder={i18n('Search Recipient Groups')}
                        onChange={event => setRecipientFilter(event.target.value?.trim())}
                        onClear={() => setRecipientFilter('')}
                        refs="search"
                    />
                </div>
                <button
                    className="recipientGroups--pageHeader__exportButton"
                    onClick={() => exportRecipientsToCsv(localRecipientGroups)}
                >
                    <i className="far fa-file-export" />
                    <span>{i18n('Export Groups')}</span>
                </button>
            </div>
            <ModalRecipientGroup
                showingModal={newRecipientModal}
                isNew={true}
                onSave={newRecipient => {
                    const newGroups = [...localRecipientGroups]
                    if (!newGroups.includes(newRecipient)) {
                        newGroups.push(newRecipient)
                        setLocalRecipientGroups([...newGroups])
                    }
                    onChange([...newGroups])
                }}
                onClose={hideModal => {
                    setNewRecipientModal(hideModal)
                }}
            />

            <div className={'recipientGroups--groupsList__header'}>
                <div className={'recipientGroups--groupsList__header--section'}>{i18n('Group Name')}</div>
                <div className={'recipientGroups--groupsList__header--section'}>{i18n('Recipients number')}</div>
                <div className={'recipientGroups--groupsList__header--section'}>{i18n('Status')}</div>
                <div className={'recipientGroups--groupsList__header--section actions'}>{i18n('Actions')}</div>
            </div>

            <NoRecipientGroup open={!(localRecipientGroups.length < 1)}>
                <i className="far fa-file-search" />
                <span className="noPreview">{i18n('No recipient groups have been added')}.</span>
            </NoRecipientGroup>
            {localRecipientGroups
                .filter(recipient => {
                    if (recipientFilter === undefined || recipientFilter === '') return true
                    const name = recipient.name ? recipient.name.trim() : ''
                    const recipientEmails = recipient.recipients
                        ? recipient.recipients.map(email => email.trim()).join(',')
                        : ''
                    const lookupsString = recipient.lookups
                        ? Object.entries(recipient.lookups)
                              .filter(([key, value]) => key !== '' && key != undefined)
                              .map(([key, value]) => {
                                  return `${key},${value}`
                              })
                              .join(',')
                        : ''
                    const regexFilter = new RegExp(recipientFilter, 'i')
                    return (
                        regexFilter.test(name) || regexFilter.test(recipientEmails) || regexFilter.test(lookupsString)
                    )
                })
                .map(recipient => {
                    const index = localRecipientGroups.findIndex(r => JSON.stringify(r) === JSON.stringify(recipient))
                    return (
                        <div className={'recipientGroups--groupsList__element'} key={index}>
                            <div className={'recipientGroups--groupsList__element--section'}>{recipient.name}</div>
                            <div className={'recipientGroups--groupsList__element--section'}>
                                {recipient.recipients?.length}
                            </div>
                            <div className={'recipientGroups--groupsList__element--section'}>
                                <div className="toggleStatus">
                                    <Toggle
                                        value={recipient.enabled}
                                        onChange={value => {
                                            const newRecipientGroups = [...localRecipientGroups]
                                            newRecipientGroups[index].enabled = value
                                            setLocalRecipientGroups([...newRecipientGroups])
                                            onChange([...newRecipientGroups])
                                        }}
                                        titleComponent={
                                            <span>{i18n(`${recipient.enabled ? 'Active' : 'Inactive'}`)}</span>
                                        }
                                    />
                                </div>
                            </div>
                            <div className={'recipientGroups--groupsList__element--section--actions'} key={index}>
                                <span
                                    className={
                                        'recipientGroups--groupsList__element--section--actions--actionEditIcon far fa-edit actions-icon'
                                    }
                                    onClick={() => {
                                        setEditingGroupModal({ showing: true, i: index })
                                    }}
                                />
                                <ModalRecipientGroup
                                    showingModal={editingGroupModal.i === index && editingGroupModal.showing}
                                    onSave={newRecipient => {
                                        const newRecipientGroups = [...localRecipientGroups]
                                        newRecipientGroups[index] = newRecipient
                                        setLocalRecipientGroups([...newRecipientGroups])
                                        onChange([...newRecipientGroups])
                                    }}
                                    onClose={hideModal => {
                                        setEditingGroupModal({ ...editingGroupModal, showing: hideModal })
                                    }}
                                    recipientGroup={localRecipientGroups[index]}
                                />

                                <span
                                    className={
                                        'recipientGroups--groupsList__element--section--actions--actionTrashIcon far fa-trash-alt actions-icon'
                                    }
                                    onClick={() => {
                                        globalConfirmation.confirm(
                                            i18n(
                                                `Are you sure you want to delete the recipient group: ${recipient.name} ?`
                                            ),
                                            () => {
                                                const recipientGroups = [...localRecipientGroups]
                                                recipientGroups.splice(index, 1)
                                                setLocalRecipientGroups([...recipientGroups])
                                                onChange([...recipientGroups])
                                            }
                                        )
                                    }}
                                />
                            </div>
                        </div>
                    )
                })}
        </StyledRecipientGroups>
    )
}

const NoRecipientGroup = styled.div<{ open: boolean }>`
    display: ${({ open }) => (!open ? 'flex' : 'none')};
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 80px;
    padding-top: 56px;

    /* background: ${({ theme }) => theme['gray-100']}; */

    .noPreview {
        padding-top: 8px;
        color: ${({ theme }) => theme['gray-700']};
        text-align: center;
        font-size: 13px;
        font-style: ${({ theme }) => theme['font-semibold']};
        font-weight: 600;
        line-height: normal;
    }
    .noPreviewText {
        width: 210px;
        color: ${({ theme }) => theme['gray-600']};
        text-align: center;
        font-style: ${({ theme }) => theme['font-regular']};
        font-size: 13px;
        font-style: italic;
        font-weight: 400;
        line-height: normal;
    }

    i {
        font-size: 32px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        color: ${({ theme }) => theme['gray-500']};
    }
`

const StyledRecipientGroups = styled.div`
    .recipientGroups {
        display: flex;
        flex-direction: column;
        width: 100%;

        &--pageHeader {
            display: flex;
            width: 100%;
            margin-bottom: 15px;
            margin-top: 20px;
            font-size: 15px;
            .dropdown {
                width: 160px;
            }
            &__dropdownButton {
                margin-right: 8px;
                button {
                    background: ${({ theme }) => theme['blue-500']};
                    :hover {
                        background: ${({ theme }) => theme['blue-700']};
                    }
                    :active {
                        background: ${({ theme }) => theme['blue-400']};
                    }
                    border: 1px solid ${({ theme }) => theme['blue-700']};
                }
                i {
                    color: ${({ theme }) => theme['gray-0']};
                }
                &__btnAddRecipientGroup {
                    width: 80%;
                    height: 30px;
                    color: white;
                    font-size: 14px;
                    line-height: 16px;
                    border: 0;
                    border-radius: 3px 0px 0px 3px;
                    transition: 0.3s;
                    &--dropicon {
                        margin: 0px;
                        height: 32px;
                        width: 20%;
                        border: 0;
                        border-radius: 0px 3px 3px 0px;
                        transition: 0.3s;
                        line-height: 16px;
                        padding: 0px;
                        i {
                            margin: 0px;
                        }
                    }
                    border: none !important;
                    height: 32px;
                }
            }
            &__dropdown {
                &__menuItem1 {
                    button {
                        border: 0px;
                        width: 100%;
                        padding: 4px 12px;
                        background: ${({ theme }) => theme['gray-0']};
                        :hover {
                            background: ${({ theme }) => theme['gray-300']};
                        }
                        i {
                            margin-right: 8px;
                        }
                    }
                }
            }
            &__searchBar {
                width: calc(100% - 310px);
                height: inherit;
                margin-right: 8px;
                display: flex;
                &__search {
                    padding-top: 8px;
                }
                input,
                > span {
                    width: 100%;
                }
                .btn-clean-search {
                    position: absolute;
                    right: 20%;
                }
            }
            &__exportButton {
                height: inherit;
                background: ${({ theme }) => theme['gray-0']};
                border: 1px solid ${({ theme }) => theme['gray-400']};
                border-radius: 3px;
                :hover {
                    background: ${({ theme }) => theme['gray-300']};
                }

                i {
                    margin-right: 8px;
                }
            }
        }

        &--groupsList {
            display: grid;
            grid-template-columns: 1fr;
            margin-left: 10%;
            margin-right: 10%;

            &__header {
                display: grid;
                grid-template-columns: 45% 20% 15% 1fr;
                width: 100%;
                height: 28px;
                background: ${({ theme }) => theme['gray-100']};
                /* border-bottom: 1px solid ${({ theme }) => theme['gray-500']}; */
                border-top-right-radius: 3px;
                border-top-left-radius: 3px;
                /* border-bottom: 0px; */
                &--section {
                    display: flex;
                    flex-direction: row;
                    padding-left: 30px;
                    align-items: center;
                    font-style: normal;
                    font-weight: 600;
                    font-size: 11px;
                    color: ${({ theme }) => theme['gray-700']};

                    &.actions {
                        display: flex;
                        justify-content: right;
                        padding-right: 28px;
                    }
                }
            }
            &__element {
                display: grid;
                grid-template-columns: 45% 20% 15% 1fr;
                background-color: ${({ theme }) => theme['gray-0']};
                border-top: 1px solid ${({ theme }) => theme['gray-500']};
                padding-top: 5px;
                padding-bottom: 5px;
                width: 100%;
                min-height: 48px;
                height: auto;
                transition-duration: 0.2s;

                span.actions-icon {
                    display: none;
                }
                &:hover {
                    background-color: ${({ theme }) => theme['blue-200']};
                    span.actions-icon {
                        display: flex;
                    }
                }

                &--section {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    padding-left: 30px;
                    word-wrap: break-word;

                    &--actions {
                        display: flex;
                        align-items: center;
                        justify-content: end;
                        padding-right: 30px;

                        &--actionEditIcon {
                            font-size: 12px;
                            margin-right: 8px;
                            cursor: pointer;
                            color: ${({ theme }) => theme['blue-500']};
                            &:hover {
                                color: ${({ theme }) => theme['blue-400']};
                            }
                        }
                        &--actionTrashIcon {
                            font-size: 12px;
                            cursor: pointer;
                            color: ${({ theme }) => theme['red-500']};
                            &:hover {
                                color: ${({ theme }) => theme['red-300']};
                            }
                        }
                    }
                }
            }
        }
    }

    .recipientGroups--pageHeader__exportButton {
        width: 150px;
        height: 32px;
    }
`
